<template>
  <section class="ca-history">
    <CleanerActivityToolbar>
      <div class="ca-history__presets" role="group" :aria-label="$t('ca_view_history')">
        <button
          v-for="option in presetOptions"
          :key="option"
          type="button"
          class="ca-history__preset"
          :class="{ 'is-active': preset === option }"
          :disabled="loading"
          @click="selectPreset(option)"
        >{{ $t(`ca_hist_preset_${option}`) }}</button>
      </div>

      <div v-if="preset === 'custom'" class="ca-history__custom">
        <DatePicker
          class="ca-history__date"
          :value="pendingFrom"
          :clearable="false"
          :disabled="loading"
          :disabled-dates="isFutureDate"
          :title="$t('ca_hist_from')"
          @input="pendingFrom = normalizeDate($event)"
        />
        <span class="ca-history__dash" aria-hidden="true">–</span>
        <DatePicker
          class="ca-history__date"
          :value="pendingTo"
          :clearable="false"
          :disabled="loading"
          :disabled-dates="isFutureDate"
          :title="$t('ca_hist_to')"
          @input="pendingTo = normalizeDate($event)"
        />
        <button
          type="button"
          class="ca-history__apply"
          :class="{ 'is-ready': canApplyCustom }"
          :disabled="!canApplyCustom"
          @click="applyCustom"
        >{{ $t('ca_apply') }}</button>
        <span v-if="customRangeError" class="ca-history__range-error">{{ customRangeError }}</span>
      </div>

      <template v-slot:actions>
        <div class="ca-history__area">
          <Select
            class="ca-filters__select form-select"
            mdClass="ca-filters-menu"
            :value="workingAreaIds"
            :multiple="true"
            :disabled="loading || !workingAreas.length"
            :placeholder="$t('working_area')"
            @input="workingAreaIds = Array.isArray($event) ? $event : []"
          >
            <Option v-for="area in workingAreas" :key="area.id" :value="area.id" :label="area.area_name" />
          </Select>
        </div>
      </template>
    </CleanerActivityToolbar>

    <Loading :height="'40vh'" v-if="loading" />

    <div v-else-if="emptyState" class="ca-history__empty">
      <p class="ca-history__empty-title">{{ $t(emptyState.title) }}</p>
      <p class="ca-history__empty-desc">{{ $t(emptyState.desc) }}</p>
    </div>

    <template v-else-if="history">
      <CleanerHistoryKpis :summary="displaySummary" />

      <div class="ca-history__bar">
        <p class="ca-history__summary">
          {{ $t('ca_hist_people_count', { count: displayPeople.length }) }}
        </p>
        <input
          v-model="search"
          type="search"
          class="ca-history__search"
          :placeholder="$t('ca_hist_search')"
        />
      </div>

      <div v-if="noMatch" class="ca-history__empty ca-history__empty--inline">
        <p class="ca-history__empty-title">{{ $t('ca_hist_no_match') }}</p>
        <p class="ca-history__empty-desc">{{ $t('ca_empty_no_filter_match_hint') }}</p>
      </div>
      <CleanerHistoryTable
        v-else
        :people="displayPeople"
        :selectedKey="selectedKey"
        :sortKey="sortKey"
        @select="selectedKey = $event"
        @sort="sortKey = $event"
      />
    </template>

    <CleanerHistoryDetail
      v-if="selectedPerson"
      :person="selectedPerson"
      :dateFrom="history.range.dateFrom"
      :dateTo="history.range.dateTo"
      :gmt="history.range.gmt"
      :locale="locale"
      :hasPrev="selectedIndex > 0"
      :hasNext="selectedIndex < displayPeople.length - 1"
      @prev="selectByOffset(-1)"
      @next="selectByOffset(1)"
      @close="selectedKey = null"
    />
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { getCleanerHistory } from '../../../services/performanceService'
import Loading from '../../../../common/components/Loading'
import DatePicker from '../../../../common/components/DatePicker'
import Select from '../../../../common/components/Select'
import Option from '../../../../common/components/Option'
import CleanerActivityToolbar from '../CleanerActivityToolbar.vue'
import { getEffectiveRoomIds } from '../cleanerActivityFilter'
import { toLocalDateStr } from '../cleanerActivityUtils'
import CleanerHistoryKpis from './CleanerHistoryKpis.vue'
import CleanerHistoryTable from './CleanerHistoryTable.vue'
import CleanerHistoryDetail from './CleanerHistoryDetail.vue'
import {
  MAX_RANGE_DAYS,
  RANGE_PRESETS,
  DEFAULT_RANGE_PRESET,
  DEFAULT_HISTORY_SORT,
  presetRange,
  rangeDayCount,
  personMatchesFilter,
  sortPeople,
  summarizePeople,
} from './cleanerHistoryUtils'

export default {
  components: {
    Loading,
    DatePicker,
    Select,
    Option,
    CleanerActivityToolbar,
    CleanerHistoryKpis,
    CleanerHistoryTable,
    CleanerHistoryDetail,
  },
  props: {
    siteId: { type: [Number, String], required: true },
    /** Company-local today (`YYYY-MM-DD`); presets and the date pickers are bounded by it. */
    companyToday: { type: String, required: true },
    workingAreas: { type: Array, default: () => [] },
    workingAreaRoomMap: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      preset: DEFAULT_RANGE_PRESET,
      dateFrom: '',
      dateTo: '',
      pendingFrom: '',
      pendingTo: '',
      history: null,
      loading: false,
      requestId: 0,
      /** @type {null | 'error_forbidden' | 'error_load'} */
      errorReason: null,
      search: '',
      workingAreaIds: [],
      sortKey: DEFAULT_HISTORY_SORT,
      selectedKey: null,
      presetOptions: RANGE_PRESETS,
    }
  },
  computed: {
    ...mapGetters(['filterOptions']),
    locale() {
      return this.$i18n.locale
    },
    customRangeError() {
      if (!this.pendingFrom || !this.pendingTo) return ''
      if (this.pendingFrom > this.pendingTo) return this.$t('ca_hist_range_invalid')
      if (rangeDayCount(this.pendingFrom, this.pendingTo) > MAX_RANGE_DAYS) {
        return this.$t('ca_hist_range_too_long', { max: MAX_RANGE_DAYS })
      }
      return ''
    },
    canApplyCustom() {
      if (this.loading || !this.pendingFrom || !this.pendingTo || this.customRangeError) return false
      return this.pendingFrom !== this.dateFrom || this.pendingTo !== this.dateTo
    },
    noMatch() {
      return !!this.history && this.history.people.length > 0 && this.displayPeople.length === 0
    },
    effectiveRoomIds() {
      if (!this.workingAreaIds.length) return null
      return getEffectiveRoomIds(this.filterOptions, { workingAreaIds: this.workingAreaIds, roomIds: [] }, this.workingAreaRoomMap)
    },
    displayPeople() {
      if (!this.history) return []
      const filtered = this.history.people.filter((person) =>
        personMatchesFilter(person, this.search, this.effectiveRoomIds, this.workingAreaIds),
      )
      return sortPeople(filtered, this.sortKey)
    },
    displaySummary() {
      return summarizePeople(this.displayPeople)
    },
    selectedIndex() {
      return this.displayPeople.findIndex((person) => person.key === this.selectedKey)
    },
    selectedPerson() {
      return this.selectedIndex === -1 ? null : this.displayPeople[this.selectedIndex]
    },
    emptyState() {
      if (this.errorReason === 'error_forbidden') {
        return { title: 'ca_empty_access_denied', desc: 'ca_empty_access_denied_hint' }
      }
      if (this.errorReason) return { title: 'failed_to_load_data', desc: 'ca_hist_load_error_hint' }
      if (!this.history) return null
      if (!this.history.people.length) return { title: 'ca_hist_empty', desc: 'ca_hist_empty_hint' }
      return null
    },
  },
  watch: {
    siteId: {
      immediate: true,
      handler() {
        this.history = null
        this.selectedKey = null
        this.clearFilters()
        if (this.preset === 'custom' && this.dateFrom && this.dateTo) {
          this.fetchHistory()
          return
        }
        this.selectPreset(this.preset, true)
      },
    },
    // The parent learns the company timezone after its shifts load; re-anchor relative presets.
    companyToday() {
      if (this.preset !== 'custom') this.selectPreset(this.preset)
    },
  },
  methods: {
    isFutureDate(date) {
      const dateStr = toLocalDateStr(date)
      return !!dateStr && dateStr > this.companyToday
    },
    /** Accepts the picker's Date or a date string; yields 'YYYY-MM-DD' or ''. */
    normalizeDate(value) {
      const fromDate = toLocalDateStr(value)
      if (fromDate) return fromDate
      const match = String(value || '').match(/^\d{4}-\d{2}-\d{2}/)
      return match ? match[0] : ''
    },
    selectPreset(preset, force = false) {
      if (preset === 'custom') {
        this.preset = preset
        this.pendingFrom = this.dateFrom
        this.pendingTo = this.dateTo
        return
      }
      const range = presetRange(preset, this.companyToday)
      if (!range) return
      const unchanged = range.dateFrom === this.dateFrom && range.dateTo === this.dateTo
      this.preset = preset
      if (unchanged && !force) return
      this.dateFrom = range.dateFrom
      this.dateTo = range.dateTo
      this.fetchHistory()
    },
    applyCustom() {
      if (!this.canApplyCustom) return
      this.dateFrom = this.pendingFrom
      this.dateTo = this.pendingTo
      this.fetchHistory()
    },
    clearFilters() {
      this.search = ''
      this.workingAreaIds = []
      this.sortKey = DEFAULT_HISTORY_SORT
    },
    selectByOffset(offset) {
      const next = this.displayPeople[this.selectedIndex + offset]
      if (next) this.selectedKey = next.key
    },
    async fetchHistory() {
      const requestId = ++this.requestId
      this.loading = true
      this.errorReason = null
      try {
        const history = await getCleanerHistory(this.siteId, { dateFrom: this.dateFrom, dateTo: this.dateTo })
        if (requestId !== this.requestId) return
        this.history = history
        if (!history.people.some((person) => person.key === this.selectedKey)) this.selectedKey = null
      } catch (error) {
        if (requestId !== this.requestId) return
        console.error('[CleanerHistory] fetch failed', error)
        this.history = null
        this.selectedKey = null
        this.errorReason = error && error.status === 403 ? 'error_forbidden' : 'error_load'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use "../caControls" as *;

.ca-history {
  &__presets {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    align-self: flex-end;
    min-height: 34px;
    gap: 6px;
  }

  &__preset {
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid #e1e8ed;
    border-radius: 999px;
    background: #fff;
    color: #2c3e50;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      border-color: #cfd6de;
    }

    &.is-active {
      background: #2c3e50;
      border-color: #2c3e50;
      color: #fff;
    }

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  &__custom {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__dash {
    color: #9aa5b1;
    font-weight: 600;
  }

  :deep(.ca-history__date.md-datepicker) {
    @include ca-compact-datepicker;
    width: 140px;
  }

  &__apply {
    @include ca-apply-button('&.is-ready');
  }

  &__range-error {
    flex-basis: 100%;
    font-size: 0.78rem;
    color: #c0392b;
  }

  &__bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px 16px;
    flex-wrap: wrap;
    margin: 14px 0 8px;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 8px;
    margin: 0;
    font-size: 0.8rem;
    color: #7f8c8d;
  }

  &__area {
    width: 220px;
  }

  &__search {
    flex: 0 1 260px;
    min-height: 34px;
    padding: 0 10px;
    border: 1px solid #e1e8ed;
    border-radius: 6px;
    background: #fff;
    font-size: 0.8125rem;
    color: #2c3e50;

    &:focus {
      outline: none;
      border-color: #5f9b46;
      box-shadow: 0 0 0 2px rgba(95, 155, 70, 0.12);
    }
  }

  &__empty {
    @include ca-empty-state;

    &--inline {
      height: 24vh;
    }
  }

  :deep(.ca-filters__select.select-container.md-field) {
    @include ca-compact-select;
  }
}

@media (max-width: 767px) {
  .ca-history {
    &__presets {
      width: 100%;
    }

    &__custom {
      width: 100%;
    }

    :deep(.ca-history__date.md-datepicker) {
      flex: 1 1 0;
      width: auto;
    }

    &__area {
      width: 100%;
    }

    &__search {
      flex: 1 1 100%;
      max-width: none;
    }
  }
}
</style>
